import Shepherd from 'shepherd.js';
import { TOUR_BUTTONS } from '../shepherd-defaults';
import {
  lockHomeHeaderNotificationBells,
  unlockHomeHeaderNotificationBells,
} from '../home-header-notifications.shepherd';
import { SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS } from '../shepherd-tour-field-lock';
import { waitForElement } from '../shepherd.utils';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

export const HOME_PAGE_TOUR = 'home-page';

export const HOME_HEADER_NOTIFICATIONS_STEP_ID = 'home-header-notifications';

/** Bell is view-only on this step (messages tour still allows tapping the bell). */
export const HOME_NOTIFICATIONS_STEP_BODY_CLASS = 'shepherd-home-header-notifications-step';

function setHomeNotificationsStepBodyClass(active: boolean): void {
  document.body.classList.toggle(HOME_NOTIFICATIONS_STEP_BODY_CLASS, active);
}

function syncHomeNotificationsBellLock(active: boolean): void {
  if (active) {
    lockHomeHeaderNotificationBells();
    return;
  }
  unlockHomeHeaderNotificationBells();
}

/**
 * Home dashboard tutorial (`/` or `/home`): Golfer nearby card → Post activity (header or footer) →
 * Messages (mobile footer when shown) → Notifications bell. Skipped steps use `showOn`.
 */
export function buildHomePageTour(tour: ShepherdTour): void {
  tour.on('show', (ev: { step?: { id?: string } }) => {
    const isNotificationsStep = ev?.step?.id === HOME_HEADER_NOTIFICATIONS_STEP_ID;
    setHomeNotificationsStepBodyClass(isNotificationsStep);
    syncHomeNotificationsBellLock(isNotificationsStep);
  });

  tour.on('hide', (ev: { step?: { id?: string } }) => {
    if (ev?.step?.id === HOME_HEADER_NOTIFICATIONS_STEP_ID) {
      setHomeNotificationsStepBodyClass(false);
      syncHomeNotificationsBellLock(false);
    }
  });

  tour.on('complete', () => {
    setHomeNotificationsStepBodyClass(false);
    syncHomeNotificationsBellLock(false);
  });
  tour.on('cancel', () => {
    setHomeNotificationsStepBodyClass(false);
    syncHomeNotificationsBellLock(false);
  });

  tour.addStep({
    id: 'home-find-local-golfers',
    title: 'Tip',
    text: 'See what golfers near you are organizing.',
    attachTo: { element: '.golfer-nearby-card', on: 'bottom' },
    buttons: [TOUR_BUTTONS.skip, TOUR_BUTTONS.next],
    beforeShowPromise() {
      return waitForElement('.golfer-nearby-card', 4000).then(() => undefined);
    },
  });

  tour.addStep({
    id: 'home-desktop-post-activity',
    title: 'Tip',
    text: 'No plans this week? Create a golf activity in minutes.',
    attachTo: { element: '[data-tour="home-nav-post-activity"]', on: 'bottom' },
    buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.next],
    showOn: () => !!document.querySelector('[data-tour="home-nav-post-activity"]'),
    canClickTarget: true,
    beforeShowPromise() {
      return waitForElement('[data-tour="home-nav-post-activity"]', 2500).then(() => undefined);
    },
  });

  tour.addStep({
    id: 'home-mobile-footer-post',
    title: 'Tip',
    text: 'No plans this week? Create a golf activity in minutes.',
    attachTo: { element: '[data-tour="home-footer-post"]', on: 'top' },
    buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.next],
    showOn: () => !!document.querySelector('[data-tour="home-footer-post"]'),
    canClickTarget: true,
    beforeShowPromise() {
      return waitForElement('[data-tour="home-footer-post"]', 2500).then(() => undefined);
    },
  });

  tour.addStep({
    id: 'home-mobile-footer-messages',
    title: 'Tip',
    text: 'Golf conversations made easy.',
    attachTo: { element: '[data-tour="home-footer-messages"]', on: 'top' },
    buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.next],
    showOn: () => !!document.querySelector('[data-tour="home-footer-messages"]'),
    canClickTarget: true,
    beforeShowPromise() {
      return waitForElement('[data-tour="home-footer-messages"]', 2500).then(() => undefined);
    },
  });

  tour.addStep({
    id: HOME_HEADER_NOTIFICATIONS_STEP_ID,
    title: 'Tip',
    text: 'Stay updated when golfers message you or request to join activities.',
    attachTo: { element: '[data-tour="header-notification-bell"]', on: 'bottom' },
    buttons: [TOUR_BUTTONS.back, TOUR_BUTTONS.finish],
    showOn: () => !!document.querySelector('[data-tour="header-notification-bell"]'),
    ...SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
    beforeShowPromise() {
      return waitForElement('[data-tour="header-notification-bell"]', 4000).then(() => {
        lockHomeHeaderNotificationBells();
        return undefined;
      });
    },
  });
}
